"use client";

import React from "react";
import { Calculator, Database, FileText, CircleQuestionMark } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How is the baseline year established for indices?",
  answer: "Each index utilizes a fixed reference period where the aggregate value is set to a baseline of 100. For the HPI and COLEX, the baseline is set at <span class='font-mono text-xs bg-muted px-1 py-0.5 rounded'>2024 = 100</span>. This normalization ensures that all subsequent movements represent precise percentage deviations from this institutional standard, allowing for direct comparability across different economic cycles.",
  icon: Calculator
}, {
  question: "What data sources inform the Rental Exposure Index?",
  answer: "The REX combines high‑frequency rental listing data with historical bond lodgement records to capture both current market conditions and underlying structural trends. A metropolitan weighting of 0.65 is applied to reflect the concentration of rental households in Australia's major cities, ensuring the index accurately represents the economic pressure experienced by urban renters.",
  icon: Database
}, {
  question: "Can media outlets reprint AIB data visualisations?",
  answer: "Yes, all AIB indices are released under a standard institutional license. Media partners may reproduce charts provided the source is cited as 'The Australian Index Bureau'. Full methodology documentation is available for technical verification and audit purposes.",
  icon: FileText
}];
export default function PayrollInquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="233541" id="technical-faq" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div ref={ref} className={`container mx-auto max-w-4xl px-4 md:px-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-lg bg-primary/5 p-3">
            <CircleQuestionMark className="size-6 text-primary" />
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-primary sm:text-4xl md:text-5xl">
            Methodology & Technical FAQ
          </h2>
          <p className="mx-auto mt-4 max-w-[42rem] text-muted-foreground font-default sm:text-xl sm:leading-8">
            Detailed insights into the data processing, baseline standards, and 
            statistical frameworks governing AIB economic indicators.
          </p>
        </div>

        <div className="border border-border bg-card p-6 shadow-sm sm:p-8">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                <AccordionTrigger className="text-base sm:text-lg hover:no-underline group py-6">
                  <div className="flex items-center gap-4 text-left">
                    <div className="flex size-10 shrink-0 items-center justify-center bg-background border border-border transition-colors group-hover:border-primary group-hover:bg-accent">
                      <faq.icon className="size-5 text-primary" />
                    </div>
                    <span className="font-default font-medium text-foreground text-[18px]">
                      {faq.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-default sm:text-base leading-relaxed pl-14 pr-4 pb-6">
                  <div dangerouslySetInnerHTML={{
                __html: faq.answer
              }} />
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
          
          <div className="mt-8 flex justify-center">
            <Link to="/MethodologyPage" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-default font-semibold text-sm tracking-tight rounded-sm hover:opacity-90 transition-opacity">
              <FileText className="size-4" />
              Download Full Documentation (PDF)
            </Link>
          </div>
        </div>
      </div>
    </section>;
}